import { useState, useCallback } from 'react';
import api, { type VaultSaveSourceDto, type VaultSaveTargetDto } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type { SavePromptOptions } from '@/components/files/file-dialog/useSaveDialogPrompt';
import type {
  CanvasNativeExportFormat,
  FiasUploadFormat,
  ProcessImageRequest,
  ProcessImageResponse,
} from '../types';
import { saveBlobLocally, type SaveBlobLocallyResult } from '../lib/save-blob-locally';

const UPLOAD_MIME: Record<FiasUploadFormat, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  avif: 'image/avif',
};

/** A Documents folder chosen earlier in a batch (slices, artboards): the
 *  rest of the batch saves there too, keeping both on a name clash. */
export interface FiasSaveFolder {
  holderId: string;
  folderId: string | null;
}

export type FiasExportResult = ProcessImageResponse & {
  /** Where a Save-As went, for the rest of a batch. */
  savedInto?: FiasSaveFolder;
};

/** The Save dialog's prompt, as the editor page provides it. */
export type PromptSave = <T>(
  options: SavePromptOptions<T>,
) => Promise<(T & { documentId: string; name: string }) | null>;

/** Human label for the FSA file-picker's format dropdown. */
const FORMAT_DESCRIPTIONS: Record<CanvasNativeExportFormat, string> = {
  png: 'PNG image',
  jpeg: 'JPEG image',
  webp: 'WebP image',
  avif: 'AVIF image',
  tiff: 'TIFF image',
};

interface UseImageExportOptions {
  /** Opens the host Save dialog for a Save-As into Fias. */
  promptSave?: PromptSave;
  /** The Vault document the active tab was opened from, if any: a Save-As
   *  sends it so the new file keeps at least its tier (ADR-039 amendment 2). */
  saveSource?: () => VaultSaveSourceDto | null;
  /** A Save-As made a new document: it becomes the tab's source. */
  onSavedAs?: (source: VaultSaveSourceDto) => void;
  exportToDataURL: (
    format: CanvasNativeExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => string;
  exportToBlob: (
    format: CanvasNativeExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => Promise<Blob | null>;
}

export function useImageExport({
  exportToBlob,
  promptSave,
  saveSource,
  onSavedAs,
}: UseImageExportOptions) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /** Save the canvas locally. Preferred path: File System Access API
   *  (returns a `'saved-with-handle'` result so callers can pin the
   *  handle on the active tab and let a follow-up Save write
   *  in-place). Falls back to iOS Web Share or `<a download>` —
   *  those return `'saved'` with no handle. A user-cancelled picker
   *  returns `'cancelled'` so callers can suppress success toasts. */
  const exportToLocal = useCallback(
    async (
      fileName: string,
      format: CanvasNativeExportFormat = 'png',
      quality = 1,
      target?: { width?: number; height?: number },
    ): Promise<SaveBlobLocallyResult> => {
      setIsExporting(true);
      setExportError(null);

      try {
        const blob = await exportToBlob(format, quality, target);
        if (!blob) throw new Error('Failed to export canvas');

        return await saveBlobLocally(blob, {
          fileName,
          extension: format,
          description: FORMAT_DESCRIPTIONS[format],
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed';
        setExportError(message);
        // Surface the error as a toast directly. Callers can also
        // read `exportError` from the hook, but no current consumer
        // does — without this toast, an encode failure leaves the
        // user staring at an unresponsive Save button with no UI
        // feedback. Returning `'cancelled'` so post-save side
        // effects (markClean, history snapshot) are skipped.
        toast({
          title: 'Export failed',
          description: message,
          variant: 'destructive',
        });
        return { status: 'cancelled' };
      } finally {
        setIsExporting(false);
      }
    },
    [exportToBlob],
  );

  /**
   * Send canvas data to the server for Sharp processing, and save to Fias.
   *
   *   - In place (`sourceFileId`), or no save: straight to the server.
   *   - Save-As: the host Save dialog asks where (a folder of the user's
   *     Documents), and what to do about a name clash; the route saves there.
   *     `into` skips the dialog for the rest of a batch — same folder, Keep
   *     both on a clash.
   *
   * Resolves `'cancelled'` when the user closes the Save dialog, `null` on
   * failure (with `exportError` set).
   */
  const exportToFias = useCallback(
    async (
      request: ProcessImageRequest,
      into?: FiasSaveFolder,
    ): Promise<FiasExportResult | null | 'cancelled'> => {
      setExportError(null);
      const run = async (req: ProcessImageRequest) => {
        setIsExporting(true);
        try {
          // The server's `format` is the one requested — narrowed here.
          const target = req.saveAs?.target;
          const derivedFrom = target ? (saveSource?.() ?? null) : null;
          const response = (await api.processImage({
            ...req,
            saveAs: target ? { target, ...(derivedFrom ? { derivedFrom } : {}) } : undefined,
          })) as ProcessImageResponse;
          if (target && response.fileId && !req.saveAs?.part) {
            onSavedAs?.({ documentId: response.fileId, holderId: target.holderId });
          }
          return response;
        } finally {
          setIsExporting(false);
        }
      };
      try {
        const saveAs = request.saveAs;
        if (!saveAs || request.sourceFileId) return await run(request);
        const format = request.output.format;
        const suggestedName = `${saveAs.fileName}.${format}`;
        if (into) {
          const target: VaultSaveTargetDto = {
            holderId: into.holderId,
            folderId: into.folderId,
            name: suggestedName,
            onConflict: 'keep-both',
          };
          return { ...(await run({ ...request, saveAs: { ...saveAs, target } })), savedInto: into };
        }
        if (!promptSave) throw new Error('Saving to Fias is not available here.');
        let savedInto: FiasSaveFolder | undefined;
        const saved = await promptSave({
          suggestedName,
          mimeType: UPLOAD_MIME[format],
          saver: { kind: 'user', label: 'Image Editor', memoryKey: 'arche_image_editor' },
          writer: async (target) => {
            const response = await run({ ...request, saveAs: { ...saveAs, target } });
            savedInto = { holderId: target.holderId, folderId: target.folderId };
            return {
              ...response,
              documentId: response.fileId ?? '',
              name: response.fileName ?? target.name,
            };
          },
        });
        if (!saved) return 'cancelled';
        return { ...saved, savedInto };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Server export failed';
        setExportError(message);
        return null;
      }
    },
    [promptSave, saveSource, onSavedAs],
  );

  return {
    isExporting,
    exportError,
    exportToLocal,
    exportToFias,
  };
}
